import { useCallback, useEffect, useRef, useState } from "react";
import { DocumentNote, LibraryNote, Note } from "@/types";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Markdown } from "@/components/ui/markdown";
import { MarkdownField } from "@/components/ui/markdown-field";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus,
  X,
  CornerDownLeft,
  Pin,
  PinOff,
  Loader2,
  Search,
} from "lucide-react";
import { get, post, del, put } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

interface NotesSidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  parentType: "document" | "scripture" | "quote";
  parentId: number;
  title?: string;
  /** When true, render as pane content without fixed overlay chrome */
  embedded?: boolean;
  onInsert?: (content: string) => void;
}

/** How long to wait after the last keystroke before searching the library. */
const SEARCH_DEBOUNCE_MS = 250;

type Scope = "document" | "library";

export function NotesSidebar({
  isOpen = true,
  onClose,
  parentType,
  parentId,
  title,
  embedded = false,
  onInsert,
}: NotesSidebarProps) {
  const { toast } = useToast();
  const [notes, setNotes] = useState<DocumentNote[]>([]);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState("");
  const [saving, setSaving] = useState(false);

  const [scope, setScope] = useState<Scope>("document");
  const [query, setQuery] = useState("");
  const [library, setLibrary] = useState<LibraryNote[]>([]);
  const [searching, setSearching] = useState(false);

  const numericParentId = Number(parentId);
  const active = isOpen || embedded;
  const ready = active && Number.isFinite(numericParentId);

  /**
   * The library scope only makes sense inside a document — pinning is a
   * document-to-note link, and there is nothing to pin *to* from a quote's
   * notes list.
   */
  const canPin = parentType === "document";

  const fetchNotes = useCallback(async () => {
    try {
      if (canPin) {
        // Own notes and pinned notes in one read, already labelled.
        setNotes(await get<DocumentNote[]>(`/documents/${numericParentId}/notes`));
        return;
      }
      // Scripture and quote notes have no pin table and no server-side filter,
      // so this stays the original client-side narrowing.
      const data = await get<Note[]>(`/notes?parent_type=${parentType}`);
      setNotes(
        (data || [])
          .filter((n) => Number(n.parent_id) === numericParentId)
          .map((n) => ({ ...n, source: "", relation: "own" as const })),
      );
    } catch (error) {
      console.error("Error fetching notes:", error);
      toast({
        title: "Could not load notes",
        description: error instanceof Error ? error.message : "Try again.",
        variant: "destructive",
      });
    }
  }, [canPin, numericParentId, parentType, toast]);

  useEffect(() => {
    if (ready) void fetchNotes();
  }, [ready, fetchNotes]);

  // Debounced so a search runs on the pause, not on every keystroke. The
  // request id guards against an earlier, slower response landing last and
  // overwriting results for a query the author has already moved past.
  const searchSeq = useRef(0);
  useEffect(() => {
    if (scope !== "library" || !ready) return;
    const seq = ++searchSeq.current;
    setSearching(true);
    const timer = setTimeout(() => {
      get<LibraryNote[]>(`/notes/library?q=${encodeURIComponent(query.trim())}`)
        .then((rows) => {
          if (seq === searchSeq.current) setLibrary(rows || []);
        })
        .catch(() => {
          if (seq === searchSeq.current) setLibrary([]);
        })
        .finally(() => {
          if (seq === searchSeq.current) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [scope, query, ready]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    if (!Number.isFinite(numericParentId) || numericParentId <= 0) {
      toast({
        title: "Cannot save note",
        description: "Document is not ready yet. Wait a moment and try again.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const result = await post<{ note: Note }>("/notes", {
        content: newNote.trim(),
        parent_type: parentType,
        parent_id: numericParentId,
      });
      if (!result?.note) {
        throw new Error("Server did not return the saved note");
      }
      setNotes((prev) => [
        { ...result.note, source: title || "", relation: "own" },
        ...prev,
      ]);
      setNewNote("");
      setIsAddingNote(false);
      toast({ title: "Note saved" });
    } catch (error) {
      console.error("Error adding note:", error);
      toast({
        title: "Could not save note",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    try {
      await del(`/notes/${noteId}`);
      setNotes((prev) => prev.filter((note) => note.id !== noteId));
      setLibrary((prev) => prev.filter((note) => note.id !== noteId));
      toast({ title: "Note deleted" });
    } catch (error) {
      console.error("Error deleting note:", error);
      toast({
        title: "Could not delete note",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSaveEdit = async (noteId: number) => {
    if (!editContent.trim()) return;
    try {
      const updated = await put<Note>(`/notes/${noteId}`, {
        content: editContent.trim(),
      });
      setNotes((prev) =>
        prev.map((n) => (n.id === noteId ? { ...n, ...updated } : n)),
      );
      setEditingId(null);
      setEditContent("");
      toast({ title: "Note updated" });
    } catch (error) {
      console.error("Error updating note:", error);
      toast({
        title: "Could not update note",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  const pinnedIds = new Set(notes.map((n) => n.id));

  const pin = async (note: LibraryNote) => {
    // Optimistic: the pin lands in the document list straight away, so
    // switching back shows it without a round trip.
    setNotes((prev) => [...prev, { ...note, relation: "pinned" }]);
    try {
      await post(`/documents/${numericParentId}/notes/pins`, { noteId: note.id });
    } catch (error) {
      setNotes((prev) => prev.filter((n) => n.id !== note.id));
      toast({
        title: "Could not pin that note",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  const unpin = async (note: DocumentNote) => {
    setNotes((prev) => prev.filter((n) => n.id !== note.id));
    try {
      await del(`/documents/${numericParentId}/notes/pins/${note.id}`);
    } catch (error) {
      setNotes((prev) => [...prev, note]);
      toast({
        title: "Could not unpin that note",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  if (!embedded && !isOpen) return null;

  const renderNote = (note: DocumentNote) => (
    <Card key={note.id} className="p-3">
      {editingId === note.id ? (
        <div className="space-y-2">
          <MarkdownField value={editContent} onValueChange={setEditContent} />
          <div className="flex gap-2">
            <Button type="button" size="sm" onClick={() => void handleSaveEdit(note.id)}>
              Save
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setEditingId(null)}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex justify-between items-start gap-2 mb-2">
            <Markdown content={note.content} className="text-sm min-w-0" />
            {/* A pinned note lives somewhere else. Removing it from this
                document must unpin it, never delete the original. */}
            {note.relation === "pinned" ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0"
                title="Unpin from this document"
                onClick={() => void unpin(note)}
              >
                <PinOff className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0"
                title="Delete note"
                onClick={() => void handleDeleteNote(note.id)}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground truncate">
              {note.relation === "pinned" && note.source ? (
                <span className="inline-flex items-center gap-1">
                  <Pin className="h-3 w-3 shrink-0" />
                  {note.source}
                </span>
              ) : (
                new Date(note.created_at).toLocaleString()
              )}
            </p>
            <div className="flex gap-1 shrink-0">
              {onInsert && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => onInsert(note.content)}
                >
                  <CornerDownLeft className="h-3.5 w-3.5 mr-1" />
                  Insert
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditingId(note.id);
                  setEditContent(note.content);
                }}
              >
                Edit
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );

  const documentScope = (
    <>
      <div className="flex justify-end mb-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setIsAddingNote(!isAddingNote)}
        >
          {isAddingNote ? (
            <X className="h-4 w-4 mr-2" />
          ) : (
            <Plus className="h-4 w-4 mr-2" />
          )}
          {isAddingNote ? "Cancel" : "Add Note"}
        </Button>
      </div>

      {isAddingNote && (
        <Card className="p-4 mb-4">
          <MarkdownField
            placeholder="Write your note..."
            value={newNote}
            onValueChange={setNewNote}
            className="mb-2"
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                void handleAddNote();
              }
            }}
          />
          <Button
            type="button"
            onClick={() => void handleAddNote()}
            className="w-full"
            disabled={saving || !newNote.trim()}
          >
            {saving ? "Saving…" : "Add Note"}
          </Button>
        </Card>
      )}

      <div className="space-y-3">
        {notes.map(renderNote)}
        {notes.length === 0 && !isAddingNote && (
          <p className="text-sm text-muted-foreground text-center py-6">
            No notes yet. Add one, or pin something from All notes.
          </p>
        )}
      </div>
    </>
  );

  const libraryScope = (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search all your notes…"
          className="pl-8"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {searching && library.length === 0 ? (
        <div className="flex justify-center py-8 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : library.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          {query.trim() ? "Nothing matches." : "No notes yet."}
        </p>
      ) : (
        library.map((note) => {
          const alreadyHere = pinnedIds.has(note.id);
          return (
            <Card key={note.id} className="p-3 space-y-2">
              <Markdown content={note.content} className="text-sm min-w-0" />
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground truncate">
                  {note.source}
                </span>
                <div className="flex gap-1 shrink-0">
                  {onInsert && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => onInsert(note.content)}
                    >
                      <CornerDownLeft className="h-3.5 w-3.5 mr-1" />
                      Insert
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant={alreadyHere ? "ghost" : "secondary"}
                    disabled={alreadyHere}
                    title={
                      alreadyHere
                        ? "Already in this document"
                        : "Keep this note to hand in this document"
                    }
                    onClick={() => void pin(note)}
                  >
                    <Pin className="h-3.5 w-3.5 mr-1" />
                    {alreadyHere ? "Pinned" : "Pin"}
                  </Button>
                </div>
              </div>
            </Card>
          );
        })
      )}
    </div>
  );

  const body = (
    <>
      {!embedded && (
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-semibold">Notes</h2>
            {title && <p className="text-sm text-muted-foreground">{title}</p>}
          </div>
          {onClose && (
            <Button type="button" variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

      <div className={embedded ? "space-y-4" : "flex-1 overflow-y-auto p-4"}>
        {canPin && (
          <Tabs value={scope} onValueChange={(v) => setScope(v as Scope)}>
            <TabsList className="grid grid-cols-2 w-full mb-3">
              <TabsTrigger value="document">This document</TabsTrigger>
              <TabsTrigger value="library">All notes</TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        {scope === "library" && canPin ? libraryScope : documentScope}
      </div>
    </>
  );

  if (embedded) return <div className="h-full overflow-y-auto">{body}</div>;

  return (
    <div className="fixed right-0 top-0 h-full w-80 bg-background border-l shadow-lg z-50">
      <div className="flex flex-col h-full">{body}</div>
    </div>
  );
}
